import type { PermitListResponse } from '@contracts/permitSearch/PermitListResponse';
import type { GridSortModel } from '@mui/x-data-grid-pro';
import { useGridApiRef } from '@mui/x-data-grid-pro';
import { PageLayout } from '@src/common/components/Page';
import { Stack } from '@src/common/components/Stack';
import { tableContainer } from '@src/common/components/tableStyle/TableStyle';
import { theme } from '@src/common/styles/Theme';
import { useSearchPermitsWithFees } from '@src/permits/hooks/useSearchPermitsWithFees';
import { PermitOrderDrawer } from '@src/permits/permitSearch/PermitOrderDrawer';
import { PermitSearchButtons } from '@src/permits/permitSearch/PermitSearchButtons';
import { PermitSearchExportButtons } from '@src/permits/permitSearch/PermitSearchExportButtons';
import { SortingMode } from '@src/permits/permitSearch/SortingMode';
import { sortingModeAssembler } from '@src/permits/permitSearch/SortingModeAssembler';
import type {
  SearchPermitFilters,
  SearchPermitForReportFilters,
} from '@src/permits/services/PermitApplicationClient';
import { FiltersForReportSearch } from '@src/reports/FiltersForReportSearch';
import { ResultForReportSearch } from '@src/reports/ResultForReportSearch';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

const EMPTY_FILTERS: SearchPermitForReportFilters = {
  type: [],
  client: [],
  billTo: [],
  jurisdiction: [],
  status: [],
  stateId: '',
  submissionDate: undefined,
  tractorUnitNumber: '',
  tractorLicensePlateNumber: '',
  tractorVIN: '',
};
export const Reports = () => {
  const { t } = useTranslation();
  const gridApiRef = useGridApiRef();

  const [filters, setFilters] =
    useState<SearchPermitForReportFilters>(EMPTY_FILTERS);
  const [sortModel, setSortModel] = useState<GridSortModel>([]);
  const [selectedPermits, setSelectedPermits] = useState<Set<string>>(
    new Set(),
  );
  const [permitToView, setPermitToView] = useState<
    PermitListResponse['permits'][number] | null
  >(null);
  const { searchPermits, permits, isLoading } = useSearchPermitsWithFees();
  const updateFilters = (override: Partial<SearchPermitForReportFilters>) => {
    setFilters({ ...filters, ...override });
  };

  const resetFilters = () => {
    setFilters(EMPTY_FILTERS);
    setSortModel([]);
  };

  useEffect(() => {
    if (permits?.permits) {
      const initialSelected = new Set(
        permits?.permits?.map((permit) => permit.id),
      );
      setSelectedPermits(initialSelected);
    }
  }, [permits]);

  const handleCheckboxChange = useCallback(
    (permitId: string, isChecked: boolean) => {
      setSelectedPermits((prevSelected) => {
        const newSelected = new Set(prevSelected);
        if (isChecked) {
          newSelected.add(permitId);
        } else {
          newSelected.delete(permitId);
        }
        return newSelected;
      });
    },
    [],
  );

  const handleSelectAllChange = useCallback(
    (isChecked: boolean) => {
      if (isChecked) {
        const allPermitIds = new Set(
          permits?.permits?.map((permit) => permit.id),
        );
        setSelectedPermits(allPermitIds);
      } else {
        setSelectedPermits(new Set());
      }
    },
    [permits],
  );

  const handleSortModelChange = useCallback(
    (newGridSortModel: GridSortModel) => {
      setSortModel(newGridSortModel);

      const combinedFilters: SearchPermitFilters = { ...filters };
      const sortingMode = sortingModeAssembler(newGridSortModel);
      if (sortingMode === SortingMode.SERVER && newGridSortModel.length > 0) {
        combinedFilters.sortBy = {
          field: newGridSortModel[0].field || '',
          direction: newGridSortModel[0].sort || '',
        };
      }
      searchPermits(combinedFilters);
    },
    [filters, searchPermits],
  );

  const closeOrder = useCallback(() => {
    setPermitToView(null);
  }, []);

  const handleViewOrderClick = useCallback(
    (permit: PermitListResponse['permits'][number]) => {
      setPermitToView(permit);
    },
    [],
  );
  const permitsToExport = permits?.permits
    ? permits.permits.filter((permit) => selectedPermits.has(permit.id))
    : [];

  return (
    <PageLayout title={t('reports')}>
      <Stack className={tableContainer} flex="1">
        <Stack
          paddingX={theme.spacing['5']}
          gap={theme.spacing['3']}
          paddingBottom={!permits ? theme.spacing['4'] : '0'}
        >
          <FiltersForReportSearch
            filters={filters}
            onFilterChange={updateFilters}
          />

          <PermitSearchButtons
            searchPermits={searchPermits}
            filters={filters}
            resetFilters={resetFilters}
            permits={permits}
          />
        </Stack>

        {permits && (
          <ResultForReportSearch
            permits={permits}
            isLoading={isLoading}
            onSortModelChange={handleSortModelChange}
            sortModel={sortModel}
            onCheckboxChange={handleCheckboxChange}
            onSelectAllChange={handleSelectAllChange}
            selectedPermits={selectedPermits}
            onViewOrderClick={handleViewOrderClick}
            gridApiRef={gridApiRef}
          />
        )}

        {permitsToExport.length > 0 && (
          <PermitSearchExportButtons
            permits={permitsToExport}
            gridApiRef={gridApiRef}
          />
        )}
      </Stack>
      {permitToView && (
        <PermitOrderDrawer permit={permitToView} onClose={closeOrder} />
      )}
    </PageLayout>
  );
};
